<template>
  <spinner v-if="loading" class="m-x-auto m-y-6 THREE-FOURTHS" size="100px" color="gray80"/>

  <div v-else class="TRANSACTION-CONTAINER">

    <div
        class="layout ai-center fw-wrap m-b-6 bd-b-gray30 p-b-2"
        selenium="pro-account-points">
      <div  class="layout-item flex-0-0 d-flex ai-center is-12 m-b-2 p-b-2 bd-b-gray30">
        <span class="m-x-2 LIGHT-TITLE" selenium="current-balance-heading">
          Current points balance
        </span>
        <span class="POINTS-BALANCE" selenium="pro-account-points-balance">
          {{ pointBalance | formatNumber }}
        </span>
      </div>
      <div class="layout-item  d-flex fd-column is-12 m-b-1:mo">
        <div class="d-flex jc-between">
          <div class="d-flex fd-column:mo">
            <span class="m-x-2 TEXT-SEMI">Transactions for {{monthLabel}}</span>
            <transition name="fade" mode="out-in">
              <span v-if="!loading" class="LIGHT-TITLE m-x-2:mo">{{ ofTotalLabel }}</span>
            </transition>
          </div>
          <filter-button class="BUTTON-GROUP m-r-2" @click="openFilter"/>
        </div>
        <div class="layout SCROLL-PILLS m-x-2">
          <div class="d-flex fw-wrap:t">
            <div
                v-for="transactionType in sortFilter.selectedPointTypes"
                :key="transactionType"
                class="m-t-2:mo">
              <filter-pill
                  class="m-r-1 m-y-1 PILL"
                  v-if="sortFilter.selectedPointTypes"
                  :label="typeLabels[transactionType]"
                  @click="removeFilter(transactionType)"/>
            </div>
          </div>
        </div>

      </div>
    </div>
    <div  v-if="activeTransactions && activeTransactions.length" class="m-x-2">
      <div class="layout gap-1 gap-2:t THREE-FOURTHS">
        <div
            class="layout-item is-4 is-2:t"
            selenium="table-date-heading">Date</div>
        <div
            class="layout-item is-3"
            selenium="table-source-heading">Source</div>
        <div
            class="layout-item is-3:t d-none:mo"
            selenium="table-description-heading">Description</div>
        <div
            class="layout-item is-2 is-1:t text-right"
            selenium="table-amount-heading">Amount</div>
        <div
            class="layout-item is-3 text-right"
            selenium="table-balance-heading">Balance</div>
      </div>
    </div>

    <div  v-if="activeTransactions && activeTransactions.length" class="THREE-FOURTHS bd-b-gray80 m-b-1"/>

    <div v-if="transactions && transactions.length" class="bg-white m-x-2">

      <div
          v-for="(transaction, index) in activeTransactions"
          :key="transaction.id"
          class="layout gap-1 gap-2:t THREE-FOURTHS">
        <div
            :selenium="`pro-account-points-transaction-date:${index + 1}`"
            class="layout-item is-4 is-2:t">
          {{ new Date(transaction.transaction_date_time) | timeReceivedDefault }}
        </div>
        <div
            :selenium="`pro-account-points-transaction-source:${index + 1}`"
            class="layout-item is-3">
          <span v-if="transaction.transaction_type ==='JC' || transaction.transaction_type ==='JR'">
            <span >Job Id:</span>
            <span
                @click="goToJob(transaction.reference_value)"
                selenium="link"
                class="link c-depot">
              {{ transaction.reference_value }}
            </span>
          </span>
          <span v-else></span>
        </div>
        <div
            :selenium="`pro-account-points-transaction-description:${index + 1}`"
            class="layout-item is-3:t d-none:mo">
          {{ transaction.transaction_type_description }}
        </div>
        <div
            :selenium="`pro-account-points-transaction-amount:${index + 1}`"
            :class="{ 'c-red': transaction.amount < 0 }"
            class="layout-item is-2 is-1:t text-right">
          {{ transaction.point | formatNumber }}
        </div>
        <div
            :selenium="`pro-account-points-transaction-balance:${index + 1}`"
            class="layout-item is-3 text-right">
          {{ transaction.point_balance | formatNumber }}
        </div>
      </div>


      <div v-if="total > transactions.length" class="text-center m-t-2">
        <div v-if="transactions && !activeTransactions.length" class="d-flex fd-column m-6">
          <img
              :height="$device.isMobile ? 150 : 100"
              class="m-b-2"
              src="./assets/Empty-Search.svg"/>
          <span>
            There are no results with the first {{ transactions.length }} records
          </span>
        </div>
        <button
            class="btn-secondary btn-sm"
            selenium="points-older-link"
            @click="fetchTransactions(true)">
          Load More
        </button>
      </div>
    </div>

    <div
        v-if="(total === transactions.length) && !activeTransactions.length &&
        !loading"
        class="text-center m-6:d m-1 d-flex fd-column"
        selenium="pro-account-points-notransactions">
      <img
          :height="$device.isMobile ? 150 : 100"
          class="m-x-2"
          src="./assets/No-Results-Found.svg"/>
      <span>
        There are no results with the filters you selected.<br/>
        Try to unselect them to find results
      </span>
    </div>
    <points-filter-modal
        v-if="showFilter"
        :initFilter="sortFilter"
        :selectedMonth="selectedMonth"
        :months="months"
        @close="showFilter = false"
        @filter="onFilter"
    />
  </div>
</template>

<script>
/* eslint-disable camelcase */

import {
  format,
  endOfMonth,
  startOfMonth,
  differenceInMonths,
  subMonths,
} from 'date-fns';
import { getPoints } from '@homeservices/api-kit/workorder/points';
import { mapActions } from 'vuex';
import jobParams from '@/mixins/job-params';
import pointsBalance from '@/mixins/points-balance';
import SelectBox from '@/components/SelectBox';
import FilterButton from '@/components/FilterButton';
import FilterPill from '@/components/FilterPill';
import PointsFilterModal from '@/components/pointsFilterModal';
import { TRANSACTION_TYPES } from '@/constants/transaction-types';

export default {
  name: 'account-points',
  TRANSACTION_TYPES,
  components: { SelectBox, FilterPill, FilterButton, PointsFilterModal },
  mixins: [
    jobParams,
    pointsBalance,
  ],
  data () {
    return {
      balance: 0,
      selectedMonth: startOfMonth(new Date()),
      earliestTransactionDate: null,
      loading: true,
      transactions: [],
      filteredTransactions: [],
      total: null,
      showFilter: false,
      sortFilter: {
        selectedMonth: startOfMonth(new Date()),
        sorting: 'created_dtm desc',
        selectedPointTypes: [],
      },
    };
  },
  computed: {
    months () {
      const monthsArray = [];
      if (this.earliestTransactionDate) {
        const currentDate = startOfMonth(new Date());
        const monthsCount = differenceInMonths(new Date(currentDate),
          new Date(this.earliestTransactionDate)) + 1;
        for (let i = 0; i <= monthsCount; i += 1) {
          monthsArray.push(
            {
              key: subMonths(new Date(currentDate), i),
              label: this.formatLabel(subMonths(new Date(currentDate), i)),
            }
          );
        }
      }
      return monthsArray;
    },
    pointBalance () {
      if (this.currentRawPointsBalance.results) {
        return this.currentRawPointsBalance.results[0].point_balance;
      }
      return 0;
    },
    monthLabel () {
      return this.formatLabel(this.selectedMonth);
    },
    ofTotalLabel () {
      return !this.transactions && !this.total
        ? `Displaying 0 of ${this.total}`
        : `Displaying ${this.transactions.length} of ${this.total}`;
    },
    activeTransactions () {
      return this.sortFilter.selectedPointTypes.length
        ? this.filteredTransactions
        : this.transactions;
    },
    typeLabels () {
      const labels = {};
      this.$options.TRANSACTION_TYPES.forEach((obj) => {
        labels[obj.type] = obj.label;
      });
      return labels;
    },
  },
  watch: {
    selectedMonth () {
      this.transactions = [];
      this.total = null;
      this.fetchTransactions();
    },
  },
  async mounted () {
    await this.fetchPointsBalance();
    try {
      const { earliest_transaction_date } = await this.fetchTransactions();
      this.earliestTransactionDate = earliest_transaction_date;
    } catch (e) {
      console.error(e); // eslint-disable-line no-console
    }
  },
  methods: {
    ...mapActions('dashboard/workOrderMatches', [
      'fetchWorkOrderMatches',
    ]),
    formatLabel (month) {
      return format(month, 'MMMM yyyy');
    },
    async goToJob (user_friendly_identifier) {
      const { result } = await this.fetchWorkOrderMatches({ params: { user_friendly_identifier } });
      const order_hash = result[0];
      this.$router.push({
        name: 'dashboard::pro::jobs',
        params: {
          jobId: order_hash,
          jobsStatusAlias: this.$options.JOBS_STATUS_ALIASES.search.key,
        },
      });
    },
    async fetchTransactions (concat = false) {
      this.loading = true;

      const { data } = await getPoints({
        start_date: format(startOfMonth(new Date(this.selectedMonth)), 'yyyy-MM-dd'),
        end_date: format(endOfMonth(new Date(this.selectedMonth)), 'yyyy-MM-dd'),
        size: '100',
        from: concat ? this.transactions.length : 0,
      });

      this.transactions = concat ? this.transactions.concat(data.results) : data.results || [];
      this.filteredTransactions = this.filterTransactions(this.transactions || []);
      this.total = data.total;
      this.loading = false;
      return data;
    },
    filterTransactions (transactions) {
      const list = this.sortFilter.selectedPointTypes.join();
      return transactions.filter((transaction) => {
        return list.includes(transaction.transaction_type);
      });
    },
    removeFilter (type) {
      const newFilter = this.sortFilter.selectedPointTypes.filter((t => t !== type));
      this.sortFilter.selectedPointTypes = newFilter;
      this.fetchTransactions();
    },
    onFilter (evt) {
      this.showFilter = false;
      this.sortFilter = evt.data;
      this.selectedMonth = evt.data.selectedMonth;
      this.transactions = [];
      this.filteredTransactions = [];
      this.fetchTransactions();
    },
    openFilter () {
      this.showFilter = true;
      this.$events.$emit(this.$events.POINTS_FILTER_CLICK);
    },
  },
};
</script>

<style lang="scss" scoped>
  @import "~@homeservices/design-kit/tools";
    .SELECT-BOX {
      box-shadow: none;
    }
    .LIGHT-TITLE {
      font-size: 14px;
      color: $color-gray60;
    }
    .TEXT-SEMI {
      font-size: 14px;
      font-weight: 600;
    }
    .POINTS-BALANCE {
      font-size: 24px;
    }
    .THREE-FOURTHS {

      @include desktop {
        width: 75%;
      }
    }
    .SCROLL-PILLS{
      @include mobile-only {
        overflow-x: scroll;
        display: flex;
      }
    }
    /deep/.PILL {
      border-radius: 3px;
      white-space: nowrap;
      background-color: $color-gray20;
      svg path {
        fill: $color-gray70 ;
      }
    }
    .TRANSACTION-CONTAINER {
      width: 100%;
      background-color: white;
      padding: 2 * $unit 0 * $unit;
      @include mobile-only {
        width: 100vw;
      }
    }
</style>
